import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";

import { createMember } from "@/api/members";
import type { MemberPayload } from "@/api/members";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { MemberForm } from "@/features/members/components/MemberForm";

export function AddMemberDialog() {
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();
  const queryClient = useQueryClient();
  const [householdPending, setHouseholdPending] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const createMutation = useMutation({
    mutationFn: async (payload: MemberPayload) => {
      const {
        household_id,
        household_name,
        relationship_to_household,
        is_head_of_household,
        ...memberPayload
      } = payload;

      void household_name;
      const member = await createMember(churchId!, { ...memberPayload, household_id, relationship_to_household, is_head_of_household });

      return member;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members", churchId] });
      queryClient.invalidateQueries({ queryKey: ["households", churchId] });
      setIsOpen(false);
    },
  });

  if (!permissions.has("MEMBERS_MANAGE")) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
      >
        <Plus className="h-4 w-4" />
        Add Member
      </button>

      <Modal open={isOpen} onClose={() => { if (!householdPending && !createMutation.isPending) setIsOpen(false); }} ariaLabelledBy="add-member-title" panelClassName="max-w-4xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 id="add-member-title" className="text-xl font-semibold text-white">
                  Add Member
                </h2>
                <p className="text-sm text-slate-400">
                  Add a person and optionally connect them to a household.
                </p>
              </div>

              <button
                type="button"
                disabled={householdPending || createMutation.isPending}
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <MemberForm
              onHouseholdPendingChange={setHouseholdPending}
              submitLabel="Create Member"
              isSubmitting={createMutation.isPending}
              onSubmit={(payload) => createMutation.mutate(payload)}
            />

            {createMutation.isError && (
              <p className="mt-4 text-sm text-red-400">
                Failed to create member.
              </p>
            )}
      </Modal>
    </>
  );
}